import React, { Fragment, useEffect, useState } from "react";
import {
    Typography,
    Box,
    CircularProgress,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Paper,
    Button,
    IconButton,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { deleteEmployee, fetchCompanyEmployees } from "../../api/users";
import { useAppContext } from "../../context/AppContext";
import EditIcon from "@mui/icons-material/Edit";
import EmployeeDialog from "./EmployeeDiajog";
import DeleteIcon from "@mui/icons-material/Delete";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import ConfirmDialog from "../dialogs/ConfirmDialog";

const CompanyEmployeesTab = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { setIsLoading, setEmployeesAppContext, employeesAppContext } = useAppContext();
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedId, setSelectedId] = useState(null);

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [idToDelete, setIdToDelete] = useState(null);

    useEffect(() => {

        loadEmployees();
    }, []);

    const loadEmployees = async () => {
        setIsLoading(true);
        try {
            const data = await fetchCompanyEmployees(setEmployeesAppContext);
            setEmployees(data);
        } catch (err) {
            console.error(err);
            setError(t(`errors.${err.message}`) || t("errors.general"));
        } finally {
            setIsLoading(false);
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        setIdToDelete(id);
        setConfirmOpen(true);
    };

    const handleConfirmDelete = async () => {
        setConfirmOpen(false);
        if (!idToDelete) return;

        setIsLoading(true);
        try {
            await deleteEmployee(idToDelete);
            await loadEmployees();
        } catch (err) {
            console.error("Delete employee error:", err);
            alert(t(`errors.${err.message}`) || t("errors.internal"));
        } finally {
            setIsLoading(false);
            setIdToDelete(null);
        }
    };

    return (
        <Box>
            {/* Header with button */}
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={3}
            >
                <Typography
                    variant="h5"
                    fontWeight="bold"
                    sx={{ color: "#000" }}
                >
                    {t("companySettings.employees")}
                </Typography>

                <Button
                    onClick={() => {
                        setSelectedId(null); // null = create
                        setDialogOpen(true);
                    }}
                    variant="contained"
                    sx={{
                        bgcolor: "#444",
                        color: "#fff",
                        fontWeight: 500,
                        borderRadius: 2,
                        textTransform: "none",
                        "&:hover": {
                            bgcolor: "#333",
                        },
                    }}
                >
                    {t("companyEmployees.create_employee")}
                </Button>
            </Box>

            {/* Content */}
            {loading ? (
                <CircularProgress />
            ) : error ? (
                <Typography color="error">{error}</Typography>
            ) : employees.length === 0 ? (
                <Typography sx={{ color: "#000" }}>
                    {t("companyEmployees.no_employee")}
                </Typography>
            ) : (
                <Paper elevation={2}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ color: "#000", fontWeight: 600 }}>{t("companyEmployees.name")}</TableCell>
                                <TableCell sx={{ color: "#000", fontWeight: 600 }}>{t("companyEmployees.email")}</TableCell>
                                <TableCell sx={{ color: "#000", fontWeight: 600 }}>{t("companyEmployees.role")}</TableCell>
                                <TableCell sx={{ color: "#000", fontWeight: 600 }}>{t("companyEmployees.is_active")}</TableCell>
                                <TableCell sx={{ color: "#000", fontWeight: 600 }}></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {employees.map((emp) => (
                                <TableRow key={emp.id}>
                                    <TableCell sx={{ color: "#000" }}>{emp.name}</TableCell>
                                    <TableCell sx={{ color: "#000" }}>{emp.email}</TableCell>
                                    <TableCell sx={{ color: "#000" }}>{t(`roles.${emp.role}`) || t("companyEmployees.noRole")}</TableCell>
                                    <TableCell sx={{ color: "#000" }}>{emp.is_active ? "✅" : "❌"}</TableCell>
                                    <TableCell>
                                        {emp.role !== "owner" && (
                                            <Fragment>
                                                <IconButton
                                                    color="primary"
                                                    onClick={() => {
                                                        setSelectedId(emp.id);
                                                        setDialogOpen(true);
                                                    }}
                                                    sx={{ color: "#000" }}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                                <IconButton
                                                    color="error"
                                                    onClick={() => handleDelete(emp.id)}
                                                    sx={{ ml: 1 }}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Fragment>

                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Paper>
            )}
            <EmployeeDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onSuccess={() => {
                    setDialogOpen(false);
                    setLoading(true);
                    fetchCompanyEmployees(setEmployeesAppContext).then(setEmployees).finally(() => setLoading(false));
                }}
                id={selectedId}
            />

            <ConfirmDialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={handleConfirmDelete}
                title={t("companyEmployees.confirm_dialog_title")}
                message={t("companyEmployees.confirm_dialog_message")}
            />
        </Box>
    );
};

export default CompanyEmployeesTab;
